import { useEffect, useState } from 'react';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import type { HistoryRecord } from '../data/mockHistory';
import { STATUS_LABELS, STATUS_COLORS } from '../data/mockHistory';
import { templates as TEMPLATES } from '../../promotions/data/templates';
import FormSection from '../../promotions/components/FormSection';
import type { FormValues, SectionDef } from '../../promotions/types';
import { deleteBriefingPromocao } from '../../promotions/services/briefingPromocoesApi';
import {
  exportRebaixaSimples,
  exportRebaixaProgressiva,
  exportCompreEGanhe,
  exportListaSugeridaOriginal,
  exportListaSugeridaRecompra,
  exportCupomSimples,
  exportCupomProgressivo,
  exportCashback,
  exportRebaixaCupomGeral,
} from '../../shared/utils/xlsxExport';

const XLSX_EXPORTERS: Record<string, (values: FormValues) => void> = {
  t1: exportRebaixaSimples,
  t2: exportRebaixaProgressiva,
  t3: exportCompreEGanhe,
  t4a: exportListaSugeridaOriginal,
  t4b: exportListaSugeridaRecompra,
  t5: exportCupomSimples,
  t6: exportCupomProgressivo,
  t7: exportCashback,
  t8: exportRebaixaCupomGeral,
};

interface HistoryDetailDrawerProps {
  record: HistoryRecord | null;
  open: boolean;
  onClose: () => void;
  onDeleted?: (record: HistoryRecord) => void;
  onReuse?: (record: HistoryRecord) => void;
}

function formatDate(iso: string) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function HistoryDetailDrawer({ record, open, onClose, onDeleted, onReuse }: HistoryDetailDrawerProps) {
  const [values, setValues] = useState<FormValues>({});
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<boolean[]>([]);

  const template = record ? TEMPLATES.find((t) => t.id === record.templateId) ?? null : null;
  const hasXlsx = record ? record.templateId in XLSX_EXPORTERS : false;
  const allExpanded = expandedSections.length > 0 && expandedSections.every(Boolean);
  const allCollapsed = expandedSections.length > 0 && expandedSections.every((v) => !v);

  useEffect(() => {
    if (!open || !record) return;
    setValues({ ...record.values });
    setExpandedSections((template?.sections ?? []).map(() => true));
    setError(null);
    setConfirmDeleteOpen(false);
  }, [open, record, template]);

  const handleChange = (_key: string, _value: string | string[]) => {
    // Drawer em modo somente leitura: mantém os campos controlados, mas impede qualquer alteração.
    setError(null);
  };

  const handleDeleteConfirm = async () => {
    if (!record) return;

    try {
      setDeleting(true);
      setError(null);
      await deleteBriefingPromocao(record.id);
      setConfirmDeleteOpen(false);
      onDeleted?.(record);
      onClose();
    } catch (err) {
      console.log(err);
      setError(err instanceof Error ? err.message : 'Não foi possível remover o briefing.');
    } finally {
      setDeleting(false);
    }
  };


  const handleCopyJSON = () => {
    navigator.clipboard.writeText(JSON.stringify(values, null, 2));
  };

  const handleExportXLSX = () => {
    if (!record) return;
    const exporter = XLSX_EXPORTERS[record.templateId];
    if (exporter) exporter(values);
  };

  const handleReuse = () => {
    if (!record) return;
    onReuse?.(record);
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: '100vw', sm: 560, md: 680 },
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {record && template ? (
          <>
            <Box
              sx={{
                px: 3, py: 2,
                borderBottom: '1px solid', borderColor: 'divider',
                bgcolor: 'background.paper', flexShrink: 0,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                  <Box
                    sx={{
                      width: 36, height: 36, borderRadius: 1.5,
                      bgcolor: record.templateColor,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}
                  >
                    <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '0.75rem', lineHeight: 1 }}>
                      {record.templateNumber}
                    </Typography>
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight={700} noWrap>
                      {(values.nomePromo as string) || record.templateName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {record.templateName}
                    </Typography>
                  </Box>
                </Box>
                <IconButton size="small" onClick={onClose} sx={{ flexShrink: 0, mt: -0.5 }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.5, flexWrap: 'wrap' }}>
                <Chip
                  label={STATUS_LABELS[record.status]}
                  color={STATUS_COLORS[record.status]}
                  size="small"
                  sx={{ fontWeight: 600, fontSize: '0.7rem', height: 22 }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                  <PersonOutlineIcon sx={{ fontSize: 14 }} />
                  <Typography variant="caption">{record.author}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                  <CalendarTodayOutlinedIcon sx={{ fontSize: 14 }} />
                  <Typography variant="caption">Criado {formatDate(record.createdAt)}</Typography>
                </Box>
                {record.createdAt !== record.updatedAt && (
                  <Typography variant="caption" color="text.secondary">
                    · Editado {formatDate(record.updatedAt)}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box
              sx={{
                px: 3, py: 1,
                borderBottom: '1px solid', borderColor: 'divider',
                display: 'flex', alignItems: 'center', gap: 1,
                flexWrap: 'wrap', flexShrink: 0, bgcolor: 'grey.50',
              }}
            >

              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={<RestartAltIcon fontSize="small" />}
                onClick={handleReuse}
                sx={{ textTransform: 'none', fontSize: '0.8rem', fontWeight: 700 }}
              >
                Reaproveitar
              </Button>

              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<DeleteOutlineIcon fontSize="small" />}
                onClick={() => setConfirmDeleteOpen(true)}
                disabled={deleting}
                sx={{ textTransform: 'none', fontSize: '0.8rem' }}
              >
                {deleting ? 'Removendo...' : 'Remover'}
              </Button>

              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

              <Tooltip title={allExpanded ? 'Já expandido' : 'Expandir todos'}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => setExpandedSections(template.sections.map(() => true))}
                    disabled={allExpanded}
                  >
                    <UnfoldMoreIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={allCollapsed ? 'Já recolhido' : 'Recolher todos'}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => setExpandedSections(template.sections.map(() => false))}
                    disabled={allCollapsed}
                  >
                    <UnfoldLessIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              <Box sx={{ flex: 1 }} />

              {hasXlsx && (
                <Tooltip title="Baixar XLSX">
                  <IconButton size="small" onClick={handleExportXLSX} aria-label="Baixar XLSX">
                    <Typography
                      component="span"
                      sx={{
                        px: 0.5,
                        py: 0.15,
                        borderRadius: 0.5,
                        border: '1px solid',
                        borderColor: 'success.light',
                        color: 'success.dark',
                        fontSize: '0.6rem',
                        fontWeight: 800,
                        lineHeight: 1.2,
                      }}
                    >
                      XLSX
                    </Typography>
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Copiar JSON">
                <IconButton size="small" onClick={handleCopyJSON}>
                  <ContentCopyOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                px: 2,
                py: 2,
                // Impede interação direta com inputs/selects/autocomplete/etc. dentro das seções.
                '& input, & textarea, & .MuiSelect-select, & .MuiAutocomplete-root, & .MuiCheckbox-root, & .MuiRadio-root, & .MuiSwitch-root, & .MuiSlider-root': {
                  pointerEvents: 'none',
                },
              }}
            >
              {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}
              {(template.sections as SectionDef[]).map((section, idx) => (
                <FormSection
                  key={`${section.title}-${idx}`}
                  section={section}
                  index={idx}
                  values={values}
                  onChange={handleChange}
                  accentColor={record.templateColor}
                  expanded={expandedSections[idx] ?? true}
                  onToggle={() =>
                    setExpandedSections((prev) => prev.map((v, i) => (i === idx ? !v : v)))
                  }
                />
              ))}
            </Box>
          </>
        ) : null}
      </Drawer>

      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
          <WarningAmberRoundedIcon sx={{ color: 'error.main', fontSize: 22 }} />
          <Typography variant="subtitle1" fontWeight={700}>Remover briefing</Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '0.875rem' }}>
            Deseja realmente remover este registro? Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button variant="outlined" color="inherit" onClick={() => setConfirmDeleteOpen(false)} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleting}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {deleting ? 'Removendo...' : 'Sim, remover'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
